import { useState } from "react";
import { Combobox } from "@headlessui/react";
import { ICoding } from "@/data-models/value-models";
import handleBlur from "@/util/handleBlur";
import CodeComboboxResults from "@/components/base/CodeCombobox/CodeComboboxResults";
import Chip from "@/components/base/Chip";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import useCodeCombobox from "@/components/base/CodeCombobox/useCodeCombobox";
import { FloatingPortal } from "@floating-ui/react";

export interface AddChipProps {
  active?: boolean;
  disabled?: boolean;
  valueSet?: string;
  onCodeSelect: (code: ICoding) => void;
}

export default function AddChip({
  onCodeSelect,
  active,
  disabled,
  valueSet,
}: AddChipProps) {
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation();
  const {
    query,
    handleQueryChange,
    filteredCodes,
    handleCodeChange,
    by,
    helpers,
    floatingProps,
    referenceProps,
    elements,
  } = useCodeCombobox(
    valueSet ?? "http://tiro.health/fhir/ValueSet/snomed-ct-auto-generated",
    {
      limitWidthToContainer: false,
      allowedPlacements: ["bottom-start", "bottom-end", "top-start", "top-end"],
      disabled,
    }
  );
  const handleCodeSelect = (code: ICoding | null) => {
    if (!code) return;
    onCodeSelect({
      code: code.code,
      display: code.display,
      system: code.system,
    });
    handleQueryChange("");
    setIsEditing(false);
  };

  const handleFocusLost = () => {
    setIsEditing(false);
    handleQueryChange("");
  };

  return (
    <div className="inline-block min-w-[7rem]">
      {isEditing ? (
        <Combobox
          as="div"
          by={by}
          onChange={handleCodeChange(handleCodeSelect)}
          onBlur={handleBlur(
            {
              onFocusOutside: handleFocusLost,
              onFocusNull: handleFocusLost,
            },
            { portals: [elements.floating] }
          )}
          {...referenceProps}
        >
          {({ open }) => (
            <>
              <Combobox.Input
                className="box-border rounded-full border border-gray-primary bg-white px-2 py-px text-sm"
                autoFocus
                onChange={handleQueryChange}
              />
              <FloatingPortal>
                <CodeComboboxResults
                  {...floatingProps}
                  className="max-w-md"
                  open={open && query.trim().length > 0}
                  codes={filteredCodes}
                  helpers={helpers}
                  minimal
                />
              </FloatingPortal>
            </>
          )}
        </Combobox>
      ) : (
        <Chip
          disabled={disabled}
          Icon={<PlusIcon className="inline h-4 w-4" />}
          onClick={() => setIsEditing(true)}
          active={active}
        >
          {t("add-chip-item")}
        </Chip>
      )}
    </div>
  );
}
